import { Card, H2, MCard } from '../ui.jsx';
import { C, fmt, fmtK, pct, retCol, incK } from '../lib/format.js';
import { INC, INC_LABEL } from '../lib/finance.js';

export default function Overview({ s, retGrid, setIncome, setDA, setTab, L, d }) {
  const { isNarrow, g4, g2, g6 } = L;
  const { jSPY, oSPY, jSPN, oSPN } = d;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: g4, gap: 12 }}>
        <MCard label="James's Pension" value={fmt(s.jamesPension)} sub={`PruFund Growth · ${pct(s.jamesPensionGrowthPct)} p.a.`} color={C.blue} />
        <MCard label="Joanne's Pension" value={fmt(s.joannePension)} sub={`PruFund Cautious · ${pct(s.joannePensionGrowthPct)} p.a.`} color={C.pink} />
        <MCard label="Combined ISAs" value={fmt(s.jamesISA + s.joanneISA)} sub={`VWRP · ${pct(s.isaGrowthPct)} p.a.`} color={C.teal} />
        <MCard label="Premium Bonds" value={fmt(s.jamesPB + s.joannePB)} sub={`${pct(s.pbPrizeRatePct)} prize rate`} color={C.gold} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: g4, gap: 12 }}>
        <MCard label="Cash ISA reserve" value={fmt((s.jamesCashISA || 0) + (s.joanneCashISA || 0))} sub="Excluded · for repairs/contingency" color="#8B9DC8" />
        <MCard label="Mortgage" value={fmt(s.mortgageRemaining)} sub={s.mortgageCleared ? 'Cleared ✓' : 'Offset at 0%'} color={C.steel} />
        <MCard label="Company" value={fmt(s.company * 0.9)} sub="Net of div tax" color={C.teal} />
        <MCard label="House" value={fmt(s.houseValue)} sub="Not in calcs" color={C.green} />
      </div>

      <Card>
        <H2 sub="Joint post-tax income (2026 real terms) · planning to Joanne age 90 · tap to explore">When Can We Retire?</H2>
        <div style={{ display: 'grid', gridTemplateColumns: g6, gap: 10 }}>
          {INC.map(inc => {
            const yr = retGrid[inc][90], col = retCol(yr);
            return (
              <div key={inc} onClick={() => { setIncome(inc); setDA(90); setTab('retirement'); }}
                style={{
                  background: col + '18', border: `1px solid ${col}33`, borderRadius: 10,
                  padding: '14px 8px', textAlign: 'center', cursor: 'pointer', minHeight: 88,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}>
                <div style={{ fontSize: 11, color: INC_LABEL[inc] ? C.teal : C.dim, marginBottom: 4, fontWeight: INC_LABEL[inc] ? 600 : 400 }}>
                  {incK(inc)}/yr{INC_LABEL[inc] ? ' · ' + INC_LABEL[inc] : ''}
                </div>
                <div style={{ fontSize: isNarrow ? 22 : 26, fontWeight: 700, color: col, fontFamily: "'DM Mono',monospace", lineHeight: 1 }}>
                  {yr >= 2049 ? '2049+' : yr}
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>
                  {yr < 2049 ? `J:${yr - 1972}/O:${yr - 1974}` : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: g2, gap: 14 }}>
        {['James', 'Joanne'].map(name => {
          const isJ = name === 'James', ac = isJ ? C.blue : C.pink;
          const pV = isJ ? s.jamesPension : s.joannePension;
          const isa = isJ ? s.jamesISA : s.joanneISA;
          const pb = isJ ? s.jamesPB : s.joannePB;
          const fund = isJ ? 'PruFund Growth' : 'PruFund Cautious';
          const gP = isJ ? s.jamesPensionGrowthPct : s.joannePensionGrowthPct;
          const acc = isJ ? 2029 : 2031, spYr = isJ ? jSPY : oSPY, born = isJ ? 'July 1972' : 'June 1974';
          return (
            <Card key={name} style={{ borderLeft: `3px solid ${ac}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: C.bright }}>{name}</h3>
                  <div style={{ fontSize: 12, color: C.dim }}>Born {born}</div>
                </div>
                <div style={{ background: ac + '22', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>Pension access</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: ac }}>{acc}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['Pension', fmt(pV), fund + ' · ' + pct(gP)],
                  ['S&S ISA', fmt(isa), `VWRP · ${pct(s.isaGrowthPct)} p.a.`],
                  ['Prem. Bonds', fmt(pb), pct(s.pbPrizeRatePct) + ' prize rate'],
                  ['State Pension', 'From ' + spYr, 'Triple lock 2.5%+'],
                ].map(([k, v, sub]) => (
                  <div key={k}>
                    <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.bright, fontFamily: "'DM Mono',monospace" }}>{v}</div>
                    <div style={{ fontSize: 10, color: C.dim, lineHeight: 1.4 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <Card>
        <H2>Key Milestones</H2>
        <div className="scroll-x">
          <div style={{ display: 'flex', minWidth: isNarrow ? 700 : 860, padding: '8px 0' }}>
            {[
              ['Now', 'PBs at £50k cap', 'Prizes → S&S ISA', C.gold],
              ['Oct 2026', 'Mortgage cleared', '£615/mo freed', C.teal],
              ['Dec 2026', 'Inheritance', '£33k → cash ISA', C.purp],
              ['Apr 2027', 'Joanne top-ups', fmt(s.joanneTopUpAmount) + '/yr', C.pink],
              ['~2033', 'Parity', 'Then £10k/yr each', C.blue],
              ['2031', 'Earliest retire', 'Both age 57', C.green],
              [String(jSPY), 'James SP', fmtK(jSPN) + '/yr', C.blue],
              [String(oSPY), 'Joanne SP', fmtK(oSPN) + '/yr', C.pink],
            ].map(([y, l, sub, col], idx) => (
              <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                  {idx > 0 && <div style={{ flex: 1, height: 1, background: C.bdr }} />}
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: col, flexShrink: 0, margin: '0 4px' }} />
                  {idx < 7 && <div style={{ flex: 1, height: 1, background: C.bdr }} />}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: col, fontFamily: "'DM Mono',monospace" }}>{y}</div>
                <div style={{ fontSize: 11, color: C.bright, marginTop: 2 }}>{l}</div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
